import { LoaderIcon, SearchIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import type {
  CountryCode,
  Source,
  SpatialAggregationType
} from "@/types/common";
import type {
  DatasetData,
  DatasetExploreItem,
  DatasetMetadata,
  GetDatasetListResponse,
  License
} from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import DatasetRightAttrFilter from "../datasetAttributeFilter/DatasetRightAttrFilter";
import DatasetCard from "../datasetCard";
import DatasetFilterSection from "./DataSourceFilter";
import type { DataSourceProvider } from "./DataSourceList";
import type { DatasetExplorerSessionStorageData } from "./index";

type DataExploreTabProps = {
  searchQuery: string;
  setSearchDataset: (value: string) => void;
  isLoadingDatasetList: boolean;
  isLoadingCountryData: boolean;
  datasetList: GetDatasetListResponse | undefined;
  debouncedSearchQuery: string;
  selectedDataset: DatasetExploreItem | null;
  handleSelectData: (data: DatasetExploreItem) => void;
  isUseNewDatasetExplorerModal: boolean;
  pageSize: number;
  setPageSize: (value: number) => void;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  isButtonPreviewEnabled: boolean;
  setIsPreview: (value: boolean) => void;

  // Filter Props
  countryOptions: { label: string; value: CountryCode | "" }[];
  selectedCountry: { label: string; value: CountryCode };
  setSelectedCountry: (value: { label: string; value: CountryCode }) => void;
  dataSource: Source;
  setDataSource: (value: Source) => void;
  spatialAgg: SpatialAggregationType | "all";
  setSpatialAgg: (value: SpatialAggregationType | "all") => void;
  license: License | "all";
  setLicense: (value: License | "all") => void;
  selectedDataSources: string[];
  setSelectedDataSources: (value: string[]) => void;
  dataSourceProviders: DataSourceProvider[];
  showCatchmentPopup: boolean;
  sessionStorageData: DatasetExplorerSessionStorageData;
  setSessionItem: (value: DatasetExplorerSessionStorageData) => void;

  // Right Filter Props
  selectedDatasetMetadata: DatasetMetadata | undefined | null;
  onClose: () => void;
  isLoadingDatasetData: boolean;
  datasetData?: DatasetData;
};

const TEST_ID_PREFIX = "dataset-explorer";

export const DataExploreTab = ({
  searchQuery,
  setSearchDataset,
  isLoadingDatasetList,
  isLoadingCountryData,
  datasetList,
  debouncedSearchQuery,
  selectedDataset,
  handleSelectData,
  isUseNewDatasetExplorerModal,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  isButtonPreviewEnabled,
  setIsPreview,
  countryOptions,
  selectedCountry,
  setSelectedCountry,
  dataSource,
  setDataSource,
  spatialAgg,
  setSpatialAgg,
  license,
  setLicense,
  selectedDataSources,
  setSelectedDataSources,
  dataSourceProviders,
  showCatchmentPopup,
  sessionStorageData,
  setSessionItem,
  selectedDatasetMetadata,
  onClose,
  isLoadingDatasetData,
  datasetData
}: DataExploreTabProps) => {
  return (
    <div className="flex flex-1 gap-5 p-6 pt-4 overflow-hidden mt-0">
      {/* Left content */}
      <div className="flex flex-col w-[80%] overflow-hidden">
        <div className="flex items-center gap-1 border-[1px] border-gainsboro rounded-[12px] px-3 h-[40px] shrink-0">
          <SearchIcon className="fill-light-gray w-[16px]" />
          <input
            name={`${TEST_ID_PREFIX}-search-dataset`}
            className="w-full focus:outline-none"
            placeholder="Search datasets"
            value={searchQuery}
            onChange={(e) => setSearchDataset(e.target.value)}
            data-testid={`${TEST_ID_PREFIX}-search-dataset-input`}
          />
        </div>
        <div className="flex flex-1 gap-4 mt-3 overflow-hidden">
          {/* Filter Section */}
          <DatasetFilterSection
            countryOptions={countryOptions}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            dataSource={dataSource}
            spatialAgg={spatialAgg}
            license={license}
            selectedDataSources={selectedDataSources}
            setDataSource={setDataSource}
            setSpatialAgg={setSpatialAgg}
            setCurrentPage={setCurrentPage}
            setLicense={setLicense}
            setSelectedDataSources={setSelectedDataSources}
            isUseNewDatasetExplorerModal={isUseNewDatasetExplorerModal}
            dataSourceProviders={dataSourceProviders}
            showCatchmentPopup={showCatchmentPopup}
            sessionStorageData={sessionStorageData}
            setSessionItem={setSessionItem}
          />
          {/* Data list */}
          <div className="flex flex-col w-[70%] overflow-hidden">
            <span className="text-sm shrink-0">
              {isLoadingDatasetList || isLoadingCountryData
                ? "Loading..."
                : `All Datasets (${datasetList?.data.length ?? 0} of ${
                    datasetList?.meta.pagination.total ?? 1
                  })`}
            </span>
            <div className="h-full overflow-y-auto custom-scrollbar">
              <div className="flex flex-col items-center h-full">
                <div className="grid w-full grid-cols-2 gap-3 py-2">
                  {datasetList?.data?.map((item: DatasetExploreItem) => (
                    <DatasetCard
                      key={item.id}
                      data={item}
                      isSelected={selectedDataset?.id === item.id}
                      handleSelectData={handleSelectData}
                      isUseNewDatasetExplorerModal={
                        isUseNewDatasetExplorerModal
                      }
                    />
                  ))}
                </div>
                {(isLoadingDatasetList || isLoadingCountryData) && (
                  <div className="flex flex-col items-center gap-2 my-auto text-sm text-center text-dim-gray">
                    <LoaderIcon className="animate-spin" />
                    <p className="text-xs text-txtPlaceholder">Loading...</p>
                  </div>
                )}
                {datasetList?.data.length === 0 && (
                  <div className="flex flex-col items-center gap-1 my-auto text-sm text-center text-dim-gray">
                    <SearchIcon className="w-8 fill-dim-gray" />
                    <p className="text-sm font-semibold text-txtPlaceholder">
                      Data Not Found
                    </p>
                    <p className="text-txtBody text-x">
                      We couldn't find any data matching "{debouncedSearchQuery}
                      " Try using different <br />
                      keywords to refine your search.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {/* Pagination */}
            <div className="flex-1 mt-4">
              <div
                className={twMerge(
                  "flex items-center",
                  isUseNewDatasetExplorerModal
                    ? "justify-between"
                    : "justify-center"
                )}
              >
                {isUseNewDatasetExplorerModal && (
                  <div className="flex items-center mr-auto text-sm text-txtBody">
                    <Select
                      value={String(pageSize)}
                      onValueChange={(value) => {
                        const newPageSize = Number(value);
                        setCurrentPage(1);
                        setPageSize(newPageSize);
                      }}
                    >
                      <SelectTrigger className="h-8 w-fit">
                        <SelectValue placeholder="10" />
                      </SelectTrigger>
                      <SelectContent className="z-[9999]" side="top">
                        {[10, 25, 50, 100].map((pageSize) => (
                          <SelectItem key={pageSize} value={String(pageSize)}>
                            {pageSize}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="ml-2">
                      of {datasetList?.meta.pagination.total || 1} Datasets
                    </span>
                  </div>
                )}
                <Pagination
                  currentPage={currentPage}
                  totalPages={datasetList?.meta.pagination.total_pages || 1}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
            {isUseNewDatasetExplorerModal && (
              <div className="flex justify-end mt-4">
                <Button
                  data-testid="preview-button"
                  variant="outline"
                  className="py-2.5 h-auto w-[200px] rounded-xl font-semibold text-sm"
                  onClick={() => {
                    if (isButtonPreviewEnabled) {
                      setIsPreview(true);
                    }
                  }}
                  disabled={!isButtonPreviewEnabled}
                >
                  Preview Dataset
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right content */}
      <DatasetRightAttrFilter
        key={selectedDatasetMetadata?.id}
        disabled={!selectedDataset || isLoadingDatasetData}
        source={selectedDataset?.source}
        selectedDataset={selectedDatasetMetadata ?? null}
        onClose={onClose}
        isSubscribed={selectedDataset?.is_subscribed}
        isLoading={isLoadingDatasetData}
        totalRows={datasetData?.rows?.length}
      />
    </div>
  );
};
